import React, { useState } from 'react';
import {
  Card,
  List,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Space,
  Typography,
  message,
  Popconfirm,
  Tag,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
  LinkOutlined,
  FileTextOutlined,
  EyeOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EditOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { validateEducationMaterialData, validateFileUpload, sanitizeTextInput } from '../utils/smartGoalValidation';
import { handleEducationMaterialError, handleFileUploadError, handleOfflineError, retryApiCall } from '../utils/smartGoalErrorHandling';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const EducationMaterialsManager = ({
  smartGoalId,
  patientId,
  materials = [],
  onMaterialsUpdate,
  readOnly = false
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [materialType, setMaterialType] = useState('link');
  const [fileList, setFileList] = useState([]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Check network connectivity
      if (handleOfflineError('add education material')) {
        return;
      }

      let values;
      try {
        values = await form.validateFields();
      } catch (validationError) {
        console.error('Education material form validation failed:', validationError);

        // Handle specific field validation errors
        if (validationError.errorFields && validationError.errorFields.length > 0) {
          const fieldErrors = validationError.errorFields.map(field =>
            `${field.name.join('.')}: ${field.errors.join(', ')}`
          ).join('; ');
          message.error(`Please fix the following errors: ${fieldErrors}`);
        } else {
          message.error('Please fill in all required fields');
        }
        setLoading(false);
        return;
      }

      // Additional validation for required fields
      if (!values.title || !values.title.trim()) {
        console.error('title field is missing or empty:', values.title);
        message.error('Material title is required');
        setLoading(false);
        return;
      }

      // Sanitize inputs
      let materialData = {
        type: values.type || materialType,
        title: sanitizeTextInput(values.title),
        description: values.description ? sanitizeTextInput(values.description) : ''
      };

      if ((values.type || materialType) === 'link') {
        materialData.url = values.url.trim();
      } else {
        // Handle file upload
        if (fileList.length === 0) {
          message.error('Please select a file to upload');
          setLoading(false);
          return;
        }

        // Validate file
        const fileValidation = validateFileUpload(fileList[0].originFileObj);
        if (!fileValidation.isValid) {
          message.error(`File validation failed: ${fileValidation.errors.join(', ')}`);
          setLoading(false);
          return;
        }

        try {
          // Upload file first
          const formData = new FormData();
          formData.append('file', fileList[0].originFileObj);
          formData.append('documentName', materialData.title || fileList[0].name);
          formData.append('documentType', 'file'); // Backend expects 'file' not 'education_material'
          formData.append('description', materialData.description);

          const uploadResponse = await retryApiCall(
            () => axios.post(`/patients/${patientId}/documents`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }),
            2,
            1000
          );

          materialData.fileId = uploadResponse.data.data._id;
        } catch (uploadError) {
          handleFileUploadError(uploadError, fileList[0].name);
          return;
        }
      }

      // Validate material data
      const validation = validateEducationMaterialData(materialData);
      if (!validation.isValid) {
        message.error(`Material validation failed: ${validation.errors.join(', ')}`);
        setLoading(false);
        return;
      }

      // Add education material to SMART goal
      const response = await retryApiCall(
        () => axios.post(`/smart-goals/${smartGoalId}/education-materials`, materialData),
        2,
        500
      );

      message.success('Education material added successfully');
      setModalVisible(false);
      form.resetFields();
      setFileList([]);
      setMaterialType('link'); // Reset material type to default

      if (onMaterialsUpdate) {
        onMaterialsUpdate(response.data.data.smartGoal.educationMaterials);
      }
    } catch (error) {
      handleEducationMaterialError(error, 'add', form.getFieldValue('title'));
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload change
  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // Handle material removal
  const handleRemoveMaterial = async (materialIndex) => {
    try {
      setLoading(true);
      // Note: This would need a backend endpoint to remove specific materials
      message.info('Material removal functionality needs backend implementation');
    } catch (error) {
      console.error('Error removing material:', error);
      message.error('Failed to remove material');
    } finally {
      setLoading(false);
    }
  };

  // Handle material view/download
  const handleViewMaterial = (material) => {
    if (material.type === 'link') {
      window.open(material.url, '_blank');
    } else {
      // Handle file download
      message.info('File download functionality needs backend implementation');
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isValidType = file.type === 'application/pdf' || 
                         file.type.startsWith('image/') ||
                         file.type === 'application/msword' ||
                         file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      
      if (!isValidType) {
        message.error('You can only upload PDF, Word documents, or images!');
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('File must be smaller than 10MB!');
        return false;
      }

      return false; // Prevent automatic upload
    },
    fileList,
    onChange: handleFileChange,
    maxCount: 1
  };

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined />
          Education Materials
          <Tag color="blue">{materials.length}</Tag>
        </Space>
      }
      size="small"
      className="education-materials-manager"
      extra={
        !readOnly && (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            Add Material
          </Button>
        )
      }
    >
      {materials.length > 0 ? (
        <List
          size="small"
          dataSource={materials}
          renderItem={(material, index) => (
            <List.Item
              actions={[
                <Tooltip title={material.type === 'link' ? 'Open Link' : 'Download File'}>
                  <Button
                    type="link"
                    icon={material.type === 'link' ? <EyeOutlined /> : <DownloadOutlined />}
                    onClick={() => handleViewMaterial(material)}
                    size="small"
                  />
                </Tooltip>,
                !readOnly && (
                  <Popconfirm
                    title="Remove this education material?"
                    onConfirm={() => handleRemoveMaterial(index)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Tooltip title="Remove Material">
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                      />
                    </Tooltip>
                  </Popconfirm>
                )
              ].filter(Boolean)}
            >
              <List.Item.Meta
                avatar={material.type === 'link' ? <LinkOutlined /> : <FileTextOutlined />}
                title={
                  <Space>
                    <Text strong>{material.title}</Text>
                    <Tag size="small" color={material.type === 'link' ? 'blue' : 'green'}>
                      {material.type}
                    </Tag>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={0}>
                    {material.description && (
                      <Text type="secondary">{material.description}</Text>
                    )}
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      Added {moment(material.addedAt).format('MMM DD, YYYY')} by {material.addedBy?.name}
                    </Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
          <FileTextOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
          <div>No education materials added yet</div>
          {!readOnly && (
            <Button
              type="link"
              onClick={() => setModalVisible(true)}
              style={{ padding: 0, marginTop: '8px' }}
            >
              Add the first material
            </Button>
          )}
        </div>
      )}

      {/* Add Material Modal */}
      <Modal
        title="Add Education Material"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setFileList([]);
          setMaterialType('link'); // Reset material type to default
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setModalVisible(false);
            form.resetFields();
            setFileList([]);
            setMaterialType('link');
          }}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
            Add Material
          </Button>
        ]}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="type"
            label="Material Type"
            initialValue="link"
          >
            <Select onChange={(value) => {
              setMaterialType(value);
              form.setFieldValue('type', value);
            }}>
              <Option value="link">External Link</Option>
              <Option value="file">Upload File</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input placeholder="Enter material title..." />
          </Form.Item>

          {materialType === 'link' ? (
            <Form.Item
              name="url"
              label="URL"
              rules={[
                { required: true, message: 'Please enter a URL' },
                { type: 'url', message: 'Please enter a valid URL' }
              ]}
            >
              <Input placeholder="https://..." />
            </Form.Item>
          ) : (
            <Form.Item
              label="File"
              required
            >
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>
                  Select File (PDF, Word, Images)
                </Button>
              </Upload>
              {fileList.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary">
                    Selected: {fileList[0].name}
                  </Text>
                </div>
              )}
            </Form.Item>
          )}

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Optional description..."
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default EducationMaterialsManager;
